import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  generatePDF,
  getChecklist,
  sendChecklistImageResponse,
  sendChecklistPerguntaImageResponse,
  sendChecklistResponse,
  sendChecklistSignedResponse,
} from "../../api/checklistApi";
import {
  ChecklistData,
  Action,
  Observation,
  RespondidoRespostas,
} from "../../types/checklistData";
import { ThemeProvider } from "@emotion/react";
import {
  Box,
  Container,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
  createTheme,
  Button,
  AppBar,
  Modal,
  Toolbar,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Fade,
  styled,
} from "@mui/material";
import {
  Add,
  ArrowBack,
  ArrowDownward,
  Block,
  BorderColor,
  CameraAlt,
  CameraFront,
  CameraRear,
  ChangeHistory,
  Checklist,
  Close,
  Delete,
  Done,
  Edit,
  ErrorOutline,
  PictureAsPdf,
  RadioButtonUnchecked,
} from "@mui/icons-material";
import CanvasDraw from "react-canvas-draw";
import Webcam from "react-webcam";
import { AxiosError } from "axios";
import { LoadingButton } from "@mui/lab";
import { formatPhone } from "../../services/formatDocument";
import ConfirmDialog from "../../components/Dialog";
import Checkbox from "@mui/material/Checkbox";
import { formatCurrency2 } from "../../services/formatCurrency";

const ContainerWrapper = styled(Box)(({ theme }) => ({
  position: "relative",
  paddingBottom: "0px",
  minHeight: "100dvh",
  overflow: "auto",
}));

const ChecklistPage: React.FC = () => {
  const { checklistId, vehicleId } = useParams<{
    checklistId: string;
    vehicleId: string;
  }>();
  const navigate = useNavigate();

  const [checklistData, setChecklistData] = useState<ChecklistData | null>(
    null,
  );

  const [selectedIcons, setSelectedIcons] = useState<{
    [key: number]: string | null;
  }>({});

  const [responses, setResponses] = useState<{
    [key: number]: {
      iconResponse?: string;
      textResponse?: string;
    };
  }>({});
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isSignOpen, setIsSignOpen] = useState(false);
  const [capturedImages, setCapturedImages] = useState<string[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [signed, setSigned] = useState<string>("");
  const [isFrontFacing, setIsFrontFacing] = useState(true);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfGenerated, setPdfGenerated] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const canvasRef = useRef<any>(null);
  const webcamRef = useRef<Webcam>(null);
  const [open, setOpen] = useState(false);
  const [acao, setAcao] = useState<Action | null>(null);
  const [permissao_foto, setPermissaoFoto] = useState(false);
  const [observations, setObservations] = useState<string[]>([]);
  const [isKmModalOpen, setIsKmModalOpen] = useState(false);
  const [tempKmFinal, setTempKmFinal] = useState<string>("");
  const [isChecklistRespondidoExpanded, setIsChecklistRespondidoExpanded] =
    useState(false);
  const [updatedKmFinal, setUpdatedKmFinal] = useState<number | undefined>(
    checklistData?.cab.km_final_veiculo,
  );
  const [perguntaFotos, setPerguntaFotos] = useState<{
    [key: number]: string[];
  }>({});
  const [cameraMode, setCameraMode] = useState<"geral" | "pergunta">("geral");
  const [perguntaIdAtual, setPerguntaIdAtual] = useState<number | null>(null);
  const [fotoGeralTirada, setFotoGeralTirada] = useState(false);
  const [isFotoPerguntaModalOpen, setIsFotoPerguntaModalOpen] = useState(false);
  const [perguntaIdSelecionada, setPerguntaIdSelecionada] = useState<
    number | null
  >(null);
  const [showFotos, setShowFotos] = useState(false);
  const [selectedRespostaFotos, setSelectedRespostaFotos] =
    useState<RespondidoRespostas | null>(null);
  const [selectedFullImage, setSelectedFullImage] = useState<string | null>(
    null,
  );
  const [idChecklistRespondido, setIdChecklistRespondido] = useState<
    string | null
  >(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const idRespondido = params.get("id_respondido") || null;
    setIdChecklistRespondido(idRespondido);
  }, []);

  const defaultTheme = createTheme({
    typography: {
      fontFamily: "Poppins, sans-serif",
    },
  });

  const handleOpenFotoPerguntaModal = (perguntaId: number) => {
    setPerguntaIdSelecionada(perguntaId);
    setIsFotoPerguntaModalOpen(true);
  };

  const handleCloseFotoPerguntaModal = () => {
    setIsFotoPerguntaModalOpen(false);
    setPerguntaIdSelecionada(null);
  };

  const handleOpenKmModal = () => {
    setTempKmFinal(checklistData?.cab.km_final_veiculo?.toString() || "");
    setIsKmModalOpen(true);
  };

  const handleCloseKmModal = () => {
    setIsKmModalOpen(false);
  };

  const handleExpandClick = () => {
    setIsExpanded(!isExpanded);
  };

  const capture = () => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc) {
        if (cameraMode === "geral") {
          setCapturedImages((prevImages) => [...prevImages, imageSrc]);
          setFotoGeralTirada(true);
        } else if (cameraMode === "pergunta" && perguntaIdAtual !== null) {
          setPerguntaFotos((prev) => ({
            ...prev,
            [perguntaIdAtual]: [...(prev[perguntaIdAtual] || []), imageSrc],
          }));
        }
        setSnackbarOpen(true);
      }
      setIsCameraOpen(false);
    }
  };

  const handleExcluirFoto = (perguntaId: number, index: number) => {
    setPerguntaFotos((prev) => {
      const updatedFotos = [...prev[perguntaId]];
      updatedFotos.splice(index, 1);
      return {
        ...prev,
        [perguntaId]: updatedFotos,
      };
    });
  };

  const clearImages = () => {
    setCapturedImages([]);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleOpenCamera = (
    mode: "geral" | "pergunta",
    perguntaId?: number | null,
  ) => {
    setCameraMode(mode);
    if (
      mode === "pergunta" &&
      perguntaId !== null &&
      perguntaId !== undefined
    ) {
      setPerguntaIdAtual(perguntaId);
    } else {
      setPerguntaIdAtual(null);
    }
    setIsCameraOpen(true);
  };

  const handleCloseCamera = () => {
    setIsCameraOpen(false);
  };

  const handleOpenSign = () => {
    if (signed) {
      setIsSignOpen(true);
    } else {
      setIsSignOpen(true);
    }
  };

  const handleCloseSign = () => {
    setIsSignOpen(false);
    if (!signed) {
      handleClearSignature();
    }
  };

  const toggleCameraFacing = () => {
    setIsFrontFacing(!isFrontFacing);
  };

  const handleExport = () => {
    const base64 = canvasRef.current.canvasContainer.childNodes[1].toDataURL();

    setSigned(base64);
    setIsSignOpen(false);
    toast.success("Assinatura salva com sucesso!");
  };

  const handleClearSignature = () => {
    setSigned("");
    setIsSignOpen(false);
  };

  const appBarHeight = checklistData?.cab.acoes.some(
    (a) => a.periodico_acao && idChecklistRespondido,
  )
    ? 230
    : 160;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("Token de autenticação não encontrado");
        }
        if (!checklistId || !vehicleId) {
          throw new Error("Parâmetros de rota ausentes");
        }
        const data = await getChecklist(token, checklistId, vehicleId);
        setChecklistData(data);
        const act = data?.cab.acoes.find(
          (acao: any) => acao.id_checklist === data?.checklist.id,
        );
        setPermissaoFoto(act.entrega_acao);
        setAcao(act);
      } catch (error) {
        if (error instanceof AxiosError && error.response) {
          const errorMessage = error.response.data.error;
          toast.error(errorMessage);
        } else {
          toast.error("Erro ao buscar checklist");
        }
        navigate("/veiculos");
      }
    };

    fetchData();
  }, [checklistId, navigate, vehicleId]);

  const isPerguntaRespondida = (perguntaId: number) => {
    const pergunta = checklistData?.checklist.grupo_perguntas
      .flatMap((grupo) => grupo.perguntas)
      .find((pergunta) => pergunta.id === perguntaId);

    if (!pergunta) return false;

    const hasIconResponse = !!responses[perguntaId]?.iconResponse;
    const hasTextResponse = !!responses[perguntaId]?.textResponse;

    if (pergunta.obrigatorio_checklist_pergunta) {
      return hasIconResponse;
    }

    return hasIconResponse || hasTextResponse;
  };

  const handleIconClick = (descricao: string, perguntaId: number) => {
    if (descricao === "Desc") return;

    const isDeselecting = selectedIcons[perguntaId] === descricao;

    setSelectedIcons((prevState) => ({
      ...prevState,
      [perguntaId]: isDeselecting ? null : descricao,
    }));

    const selectedQuestion = checklistData?.checklist.grupo_perguntas
      .flatMap((grupo) => grupo.perguntas)
      .find((pergunta) => pergunta.id === perguntaId);

    if (selectedQuestion) {
      const selectedResponse = selectedQuestion.respostas.find(
        (resposta) => resposta.descricao_checklist_resposta === descricao,
      );

      if (selectedResponse) {
        setResponses((prevResponses) => ({
          ...prevResponses,
          [perguntaId]: {
            ...prevResponses[perguntaId],
            iconResponse: isDeselecting
              ? undefined
              : selectedResponse.valor_checklist_respostas,
          },
        }));

        const updatedQuestion = {
          ...selectedQuestion,
          respostas: selectedQuestion.respostas.map((resposta) => ({
            ...resposta,
            isSelected:
              resposta.descricao_checklist_resposta === descricao
                ? !isDeselecting
                : resposta.descricao_checklist_resposta === "Desc"
                  ? resposta.isSelected
                  : false,
          })),
        };

        setChecklistData((prevData) => {
          if (!prevData) return null;
          const updatedGroups = prevData.checklist.grupo_perguntas.map(
            (grupo: any) => ({
              ...grupo,
              perguntas: grupo.perguntas.map((pergunta: any) =>
                pergunta.id === perguntaId ? updatedQuestion : pergunta,
              ),
            }),
          );
          return {
            ...prevData,
            checklist: {
              ...prevData.checklist,
              grupo_perguntas: updatedGroups,
            },
          };
        });
      }
    }
  };

  const handleClose = (confirm: boolean) => {
    if (confirm) {
      saveCheck();
    } else {
      setLoading(false);
    }
    setOpen(false);
  };

  const areAllRequiredQuestionsAnswered = () => {
    const checklistId = checklistData?.checklist.id;
    const action = checklistData?.cab.acoes.find(
      (acao) => acao.id_checklist === checklistId,
    );

    if (action?.entrega_acao) {
      return { allAnswered: true, missingPhotos: false };
    }

    let allAnswered = true;
    let missingPhotos = false;

    const obrigatorioFoto =
      checklistData?.checklist.obrigatorio_foto_pergunta_checklist;

    checklistData?.checklist.grupo_perguntas.forEach((grupo: any) => {
      grupo.perguntas.forEach((pergunta: any) => {
        const hasSelectedResponse = pergunta.respostas.some(
          (resposta: any) => resposta.isSelected,
        );

        const hasTextResponse = !!responses[pergunta.id]?.textResponse;

        const isNA = pergunta.respostas.some(
          (resposta: any) =>
            resposta.isSelected &&
            resposta.descricao_checklist_resposta === "NA",
        );

        const isDesc = pergunta.respostas.some(
          (resposta: any) =>
            resposta.isSelected &&
            resposta.descricao_checklist_resposta === "Desc",
        );

        const hasPhoto =
          isNA || isDesc || perguntaFotos[pergunta.id]?.length > 0;

        if (
          pergunta.obrigatorio_checklist_pergunta === true &&
          !hasSelectedResponse &&
          !hasTextResponse
        ) {
          allAnswered = false;
        }

        if (
          obrigatorioFoto === "obrigatorio" &&
          !hasPhoto &&
          !isNA &&
          !isDesc
        ) {
          missingPhotos = true;
        }

        if (
          obrigatorioFoto === "permite" &&
          pergunta.obrigatorio_checklist_pergunta === true &&
          !hasPhoto &&
          !isNA &&
          !isDesc
        ) {
          missingPhotos = true;
        }
      });
    });

    return { allAnswered, missingPhotos };
  };

  const isCameraDisabled = (perguntaId: number) => {
    const pergunta = checklistData?.checklist.grupo_perguntas
      .flatMap((grupo) => grupo.perguntas)
      .find((pergunta) => pergunta.id === perguntaId);

    if (!pergunta) return true;

    const obrigatorioFoto =
      checklistData?.checklist.obrigatorio_foto_pergunta_checklist;

    const isNASelected = pergunta.respostas.some(
      (resposta) =>
        resposta.isSelected && resposta.descricao_checklist_resposta === "NA",
    );

    if (isNASelected) {
      return true;
    }

    if (obrigatorioFoto === "sem_foto") {
      return true;
    }

    const hasSelectedResponse = pergunta.respostas.some(
      (resposta) =>
        resposta.isSelected && resposta.descricao_checklist_resposta !== "NA",
    );

    const hasTextResponse = !!responses[perguntaId]?.textResponse;

    return !(hasSelectedResponse || hasTextResponse);
  };

  const handleAddObservation = () => {
    setObservations([...observations, ""]);
  };

  const handleRemoveObservation = (index: number) => {
    const newObservations = observations.filter((_, i) => i !== index);
    setObservations(newObservations);
  };

  const handleObservationChange = (index: number, value: string) => {
    const newObservations = [...observations];
    newObservations[index] = value;
    setObservations(newObservations);
  };

  const handleSendResponses = async () => {
    try {
      if (!checklistId || !vehicleId) {
        throw new Error("Parâmetros de rota ausentes");
      }
      if (!checklistData) {
        throw new Error("Dados do checklist não encontrados");
      }

      const { allAnswered, missingPhotos } = areAllRequiredQuestionsAnswered();

      if (!allAnswered) {
        throw new Error(
          "Preencha todas as perguntas obrigatórias antes de salvar o checklist.",
        );
      }

      if (missingPhotos) {
        throw new Error("É obrigatório capturar fotos em todas as perguntas!");
      }

      const quantidadeMinimaFotos =
        checklistData.checklist.quantidade_minima_foto_checklist || 0;

      if (capturedImages.length < quantidadeMinimaFotos) {
        throw new Error(
          `É necessário tirar pelo menos ${quantidadeMinimaFotos} fotos para enviar o checklist.`,
        );
      }

      const action = checklistData.cab.acoes.find(
        (acao) => acao.id_checklist === parseInt(checklistId),
      );
      const requiresSignature =
        checklistData?.checklist.assinatura_checklist === true;

      if (requiresSignature && !signed) {
        throw new Error("Necessário assinatura do cliente antes de salvar.");
      }

      if (action?.entrega_acao && Object.keys(responses).length === 0) {
        setOpen(true);
      } else {
        saveCheck();
      }
    } catch (error: unknown) {
      setLoading(false);
      if (error instanceof Error) {
        toast.error(error.message);
      }
    }
  };

  const handleSaveKmFinal = () => {
    const newKm = tempKmFinal ? parseFloat(tempKmFinal) : undefined;
    setUpdatedKmFinal(newKm);
    setIsKmModalOpen(false);
  };

  const saveCheck = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Token de autenticação não encontrado");
      }

      if (!checklistId || !vehicleId) {
        throw new Error("Parâmetros de rota ausentes");
      }
      if (!checklistData) {
        throw new Error("Dados do checklist não encontrados");
      }
      const action = checklistData.cab.acoes.find(
        (acao) => acao.id_checklist === parseInt(checklistId),
      );
      if (!action) {
        throw new Error("Ação do checklist não encontrada");
      }

      const observacoesFormatadas: Observation[] = observations.map((obs) => ({
        texto: obs,
      }));

      const kmFinal = updatedKmFinal ?? checklistData.cab.km_final_veiculo;

      const dataToSend: any = {
        id_checklist: parseInt(checklistId),
        id_acao: action.id,
        id_veiculo: parseInt(vehicleId),
        resposta: prepareApiData(),
        fotos: [],
        permissao_foto: permissao_foto,
        observacoes: observacoesFormatadas,
      };

      if (kmFinal !== undefined && kmFinal !== null) {
        dataToSend.veiculo_km = kmFinal;
      }

      const response = await sendChecklistResponse(token, dataToSend);

      const captureIdChecklist = response.id;

      if (captureIdChecklist) {
        const uploadPromisesGerais = capturedImages.map(async (image) => {
          try {
            await sendChecklistImageResponse(captureIdChecklist, image, token);
          } catch (error) {}
        });

        const uploadPromisesPerguntas = Object.entries(perguntaFotos).map(
          async ([perguntaId, fotos]) => {
            for (const foto of fotos) {
              try {
                await sendChecklistPerguntaImageResponse(
                  captureIdChecklist,
                  foto,
                  token,
                  parseInt(perguntaId),
                );
              } catch (error) {}
            }
          },
        );

        if (signed) {
          await sendChecklistSignedResponse(captureIdChecklist, signed, token);
        }

        await Promise.all([
          ...uploadPromisesGerais,
          ...uploadPromisesPerguntas,
        ]);

        setCapturedImages([]);
        setPerguntaFotos({});
      }
      toast.success("Checklist preenchido com sucesso!");
      navigate("/veiculos");
    } catch (error: unknown) {
      if (error instanceof Error) {
        toast.error(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const isIOS = () =>
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

  const handleGeneratePDF = async () => {
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        throw new Error("Token de autenticação não encontrado");
      }
      if (!idChecklistRespondido) return;

      setLoading(true);

      if (isIOS()) {
        const result = await generatePDF(token, Number(idChecklistRespondido));
        const url: string = result.pdf;
        setPdfUrl(url);
        setPdfGenerated(true);
        window.open(url, "_blank", "noopener,noreferrer");
        return;
      }

      handleOpenModal();
      const pdfUrl = await generatePDF(token, Number(idChecklistRespondido));
      setPdfUrl(pdfUrl.pdf);
      setPdfGenerated(true);
    } catch (error) {
      console.error("Erro ao gerar PDF:", error);
      toast.error("Erro ao gerar PDF");
      setOpenModal(false);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleTextResponse = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    perguntaId: number,
  ) => {
    const { value } = event.target;

    setResponses((prevResponses) => ({
      ...prevResponses,
      [perguntaId]: {
        ...prevResponses[perguntaId],
        textResponse: value.trim() !== "" ? value : undefined,
      },
    }));

    setChecklistData((prevData) => {
      if (!prevData) return null;

      const updatedGroups = prevData.checklist.grupo_perguntas.map(
        (grupo: any) => ({
          ...grupo,
          perguntas: grupo.perguntas.map((pergunta: any) =>
            pergunta.id === perguntaId
              ? {
                  ...pergunta,
                  respostas: pergunta.respostas.map((resposta: any) => ({
                    ...resposta,
                    isSelected:
                      resposta.descricao_checklist_resposta === "Desc"
                        ? value.trim() !== ""
                        : resposta.isSelected,
                  })),
                }
              : pergunta,
          ),
        }),
      );

      return {
        ...prevData,
        checklist: {
          ...prevData.checklist,
          grupo_perguntas: updatedGroups,
        },
      };
    });
  };

  const prepareApiData = () => {
    const apiResponses: any[] = [];

    Object.entries(responses).forEach(([perguntaId, respostas]) => {
      if (respostas.iconResponse) {
        apiResponses.push({
          id_checklist_pergunta: Number(perguntaId),
          valor_checklist_respondido_resposta: respostas.iconResponse,
        });
      }
      if (respostas.textResponse) {
        apiResponses.push({
          id_checklist_pergunta: Number(perguntaId),
          valor_checklist_respondido_resposta: respostas.textResponse,
        });
      }
    });

    return apiResponses;
  };

  if (!checklistData) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress sx={{ color: "#000" }} />
        <Typography variant="h5" sx={{ mt: 2 }}>
          Carregando Checklist
        </Typography>
      </Box>
    );
  }

  const videoConstraints = {
    facingMode: isFrontFacing ? "user" : "environment",
  };

  const renderChecklistRespondido = () => {
    if (!checklistData?.respondido?.respostas) return null;

    const respostasAgrupadas = checklistData.respondido.respostas.reduce(
      (acc, resposta) => {
        const perguntaId = resposta.pergunta.id;
        if (!acc[perguntaId]) {
          acc[perguntaId] = {
            pergunta: resposta.pergunta,
            respostas: [],
          };
        }
        acc[perguntaId].respostas.push(resposta);
        return acc;
      },
      {} as Record<number, { pergunta: any; respostas: RespondidoRespostas[] }>,
    );

    return (
      <Box mt={4}>
        <Paper
          sx={{
            border: 1,
            cursor: "pointer",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "black",
              color: "white",
              padding: "8px 16px",
              position: "relative",
            }}
            onClick={() =>
              setIsChecklistRespondidoExpanded(!isChecklistRespondidoExpanded)
            }
          >
            <Typography variant="h6" fontWeight="bold" sx={{ flex: 1 }}>
              {checklistData.respondido.descricao_checklist} - Respondido
            </Typography>

            <IconButton
              size="small"
              sx={{
                color: "white",
                padding: 0,
                transform: isChecklistRespondidoExpanded
                  ? "rotate(180deg)"
                  : "rotate(0deg)",
                transition: "transform 0.3s ease",
                marginLeft: "8px",
              }}
              onClick={(e) => {
                e.stopPropagation();
                setIsChecklistRespondidoExpanded(
                  !isChecklistRespondidoExpanded,
                );
              }}
            >
              <ArrowDownward fontSize="small" />
            </IconButton>
          </Box>

          {isChecklistRespondidoExpanded && (
            <Box p={2}>
              {Object.values(respostasAgrupadas).map(
                ({ pergunta, respostas }) => (
                  <Box
                    key={pergunta.id}
                    sx={{
                      mb: 2,
                      borderBottom: "1px solid #eee",
                      paddingBottom: "8px",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {respostas.some((r) => r.fotos && r.fotos.length > 0) && (
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            const respostaComFotos = respostas.find(
                              (r) => r.fotos && r.fotos.length > 0,
                            );
                            if (respostaComFotos) {
                              setSelectedRespostaFotos(respostaComFotos);
                            }
                          }}
                          sx={{ mr: 1 }}
                        >
                          <CameraAlt color={showFotos ? "primary" : "action"} />
                        </IconButton>
                      )}

                      <Typography variant="body1" sx={{ flex: 1 }}>
                        {pergunta.descricao_checklist_pergunta ||
                          "Pergunta não encontrada"}
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          flexWrap: "wrap",
                        }}
                      >
                        {respostas.map((resposta) => (
                          <Box
                            key={resposta.id}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            {resposta.valor_checklist_respondido_resposta ===
                            "S" ? (
                              <RadioButtonUnchecked
                                sx={{ color: "#0a8c10", fontSize: 30 }}
                              />
                            ) : resposta.valor_checklist_respondido_resposta ===
                              "N" ? (
                              <Close sx={{ color: "#ff0a1f", fontSize: 30 }} />
                            ) : resposta.valor_checklist_respondido_resposta ===
                              "NA" ? (
                              // <Typography
                              //   variant="body1"
                              //   sx={{ color: "orange", fontWeight: "bold" }}
                              // >
                              //   NA
                              // </Typography>
                              <ChangeHistory
                                sx={{ color: "#ff780a", fontSize: 30 }}
                              />
                            ) : (
                              <Typography
                                variant="body1"
                                sx={{
                                  color: "text.primary",
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                }}
                              >
                                {resposta.valor_checklist_respondido_resposta}
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                ),
              )}
              {checklistData?.respondido?.fotos?.length > 0 && (
                <Box mt={4}>
                  <Typography variant="h6" gutterBottom>
                    Fotos Gerais do Checklist
                  </Typography>
                  <Grid container spacing={2}>
                    {checklistData.respondido.fotos.map((foto) => (
                      <Grid item xs={12} sm={6} md={4} key={foto.id}>
                        <Card>
                          <CardContent sx={{ p: 0 }}>
                            <img
                              src={`${process.env.REACT_APP_API_DOMAIN}/storage/${foto.foto_checklist_respondido_foto}`}
                              alt={`Foto geral ${foto.id}`}
                              style={{
                                width: "100%",
                                height: "auto",
                                maxHeight: "300px",
                                objectFit: "contain",
                              }}
                              onClick={() =>
                                setSelectedFullImage(
                                  `${process.env.REACT_APP_API_DOMAIN}/storage/${foto.foto_checklist_respondido_foto}`,
                                )
                              }
                            />
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              <Modal
                open={!!selectedRespostaFotos}
                onClose={() => setSelectedRespostaFotos(null)}
                aria-labelledby="modal-fotos-pergunta"
                aria-describedby="modal-fotos-pergunta-descricao"
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "90%",
                    maxWidth: "800px",
                    bgcolor: "background.paper",
                    boxShadow: 24,
                    p: 2,
                    outline: "none",
                  }}
                >
                  {selectedRespostaFotos && (
                    <>
                      <Typography variant="h6" gutterBottom textAlign="center">
                        {
                          selectedRespostaFotos.pergunta
                            .descricao_checklist_pergunta
                        }
                      </Typography>
                      <Grid container spacing={2}>
                        {selectedRespostaFotos.fotos.map((foto) => (
                          <Grid item xs={12} sm={6} md={4} key={foto.id}>
                            <Card>
                              <CardContent sx={{ p: 0 }}>
                                <img
                                  src={`${process.env.REACT_APP_API_DOMAIN}/storage/${foto.foto_checklist_respondido_foto}`}
                                  alt={`Foto da pergunta ${foto.id}`}
                                  style={{
                                    width: "100%",
                                    height: "auto",
                                    maxHeight: "300px",
                                    objectFit: "contain",
                                  }}
                                  onClick={() =>
                                    setSelectedFullImage(
                                      `${process.env.REACT_APP_API_DOMAIN}/storage/${foto.foto_checklist_respondido_foto}`,
                                    )
                                  }
                                />
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                      <Box textAlign="center" mt={2}>
                        <Button
                          variant="contained"
                          onClick={() => setSelectedRespostaFotos(null)}
                          sx={{ backgroundColor: "black" }}
                        >
                          Fechar
                        </Button>
                      </Box>
                    </>
                  )}
                </Box>
              </Modal>
              <Modal
                open={!!selectedFullImage}
                onClose={() => setSelectedFullImage(null)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "rgba(0, 0, 0, 0.9)",
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    width: "100vw",
                    height: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <IconButton
                    onClick={() => setSelectedFullImage(null)}
                    sx={{
                      position: "absolute",
                      top: 16,
                      right: 16,
                      color: "white",
                      zIndex: 10,
                      bgcolor: "rgb(255, 0, 0)",
                    }}
                  >
                    <Close />
                  </IconButton>

                  <img
                    src={selectedFullImage ?? ""}
                    alt="Imagem em tela cheia"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                    }}
                  />
                </Box>
              </Modal>
            </Box>
          )}
        </Paper>
      </Box>
    );
  };

  const renderCabDetails = () => {
    if (!checklistData || !checklistData.cab) return null;

    const keyMapping: { [key: string]: string } = {
      chassi_veiculo: "V.I.N",
      placa_veiculo: "Placa",
      data_recebimento_veiculo: "Data de Recebimento",
      km_veiculo: "Km Inicial",
      localidade_veiculo: "Localidade",
      codigo_radio_veiculo: "Código do Rádio",
      ano_veiculo: "Ano do Veículo",
      data_armazenagem_veiculo: "Data de Armazenagem",
      km_final_veiculo: "Km Final",
      descricao_cor: "Cor",
      descricao_modelo: "Modelo",
      descricao_ano_modelo: "Ano do Modelo",
      descricao_local: "Local de Armazenagem",
      nome_cliente: "Nome do Cliente",
      telefone_contato_cliente: "Telefone",
      empresa_nome: "Empresa Localizada",
      usuario: "Usuário",
    };

    const initialVehicleDetails = {
      chassi_veiculo: checklistData.cab.chassi_veiculo,
      placa_veiculo: checklistData.cab.placa_veiculo,
      km_veiculo: checklistData.cab.km_veiculo,
      descricao_local: checklistData.cab.descricao_local,
      descricao_cor: checklistData.cab.descricao_cor,
      usuario: checklistData.usuario,
    };

    return (
      <>
        <ConfirmDialog
          open={open}
          title="Confirmar"
          content="Salvar sem roteiro de entrega?"
          onClose={handleClose}
        />
        <Grid container alignItems="center">
          <Grid item xs={12}>
            <Paper
              sx={{
                border: 1,
                height: isExpanded ? "auto" : "auto",
                cursor: "pointer",
              }}
              onClick={handleExpandClick}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  position: "relative",
                  width: "100%",
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  bgcolor={"black"}
                  component={"span"}
                  color={"white"}
                  textAlign={"center"}
                  sx={{ flex: 1 }}
                >
                  Informações
                </Typography>
                <IconButton
                  sx={{
                    position: "absolute",
                    top: "50%",
                    right: "5px",
                    transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.3s ease-in-out",
                    mt: -2.6,
                  }}
                  onClick={handleExpandClick}
                >
                  <ArrowDownward sx={{ color: "white" }} />
                </IconButton>
              </Box>
              <Typography
                variant="body1"
                textAlign={"center"}
                component={"span"}
                mt={1.7}
                sx={{ padding: 1 }}
              >
                {isExpanded ? (
                  <>
                    <Grid item={true} container spacing={2}>
                      {Object.keys(checklistData.cab)
                        .filter(
                          (key) =>
                            !key.includes("id") &&
                            !key.includes("entrega_veiculo") &&
                            !key.includes("somente_atendimento_veiculo") &&
                            !key.includes("tem_checklist_agrupado") &&
                            !key.includes("recusa_veiculo") &&
                            !key.includes("acoes") &&
                            !key.includes("transferencia_veiculo") &&
                            !key.includes("grupo") &&
                            !key.includes("permite_fotos_veiculo") &&
                            checklistData.cab[
                              key as keyof typeof checklistData.cab
                            ] !== null &&
                            key !== "cliente",
                        )
                        .map((key) => {
                          let value = JSON.stringify(
                            checklistData.cab[
                              key as keyof typeof checklistData.cab
                            ],
                          ).replace(/"/g, "");
                          if (key === "data_recebimento_veiculo") {
                            const date = new Date(value);
                            if (!isNaN(date.getTime())) {
                              value = date.toLocaleString("pt-BR");
                            }
                          } else if (key === "data_armazenagem_veiculo") {
                            const date = new Date(value);
                            if (!isNaN(date.getTime())) {
                              value = date.toLocaleDateString("pt-BR");
                            }
                          }

                          return (
                            <Grid
                              item={true}
                              xs={12}
                              sm={6}
                              md={4}
                              lg={4}
                              sx={{
                                borderRight: "1px solid #ccc",
                                paddingBottom: 1,
                              }}
                              key={key}
                            >
                              <Typography fontWeight={"bold"} fontSize={16}>
                                {keyMapping[key] || key}
                              </Typography>

                              <Typography>
                                {key === "km_final_veiculo"
                                  ? (updatedKmFinal ?? value)
                                  : value}
                                {key === "km_final_veiculo" && (
                                  <IconButton
                                    size="small"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleOpenKmModal();
                                    }}
                                    sx={{ ml: 1 }}
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                )}
                              </Typography>
                            </Grid>
                          );
                        })}
                      {checklistData.cab.cliente && (
                        <Grid
                          item={true}
                          xs={12}
                          sm={6}
                          md={4}
                          lg={4}
                          sx={{
                            borderRight: "1px solid #ccc",
                            paddingBottom: 1,
                          }}
                        >
                          <Typography fontWeight={"bold"} fontSize={16}>
                            {keyMapping["nome_cliente"]}
                          </Typography>
                          <Typography>
                            {checklistData.cab.cliente.nome_cliente}
                          </Typography>{" "}
                          <Typography fontWeight={"bold"} fontSize={16} mt={2}>
                            {keyMapping["telefone_contato_cliente"]}
                          </Typography>
                          <Typography>
                            {formatPhone(
                              checklistData.cab.cliente
                                .telefone_contato_cliente,
                            )}
                          </Typography>
                        </Grid>
                      )}
                      {checklistData.usuario && (
                        <Grid
                          item={true}
                          xs={12}
                          sm={6}
                          md={4}
                          lg={4}
                          sx={{
                            borderRight: "1px solid #ccc",
                            paddingBottom: 1,
                          }}
                        >
                          <Typography fontWeight={"bold"} fontSize={16}>
                            {keyMapping["usuario"] || "Usuário"}
                          </Typography>
                          <Typography>{checklistData.usuario}</Typography>
                        </Grid>
                      )}
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid
                      item={true}
                      container
                      spacing={2}
                      justifyContent="center"
                    >
                      <Grid
                        item={true}
                        xs={12}
                        sm={6}
                        md={4}
                        lg={4}
                        sx={{ borderRight: "1px solid #ccc", paddingBottom: 1 }}
                      >
                        <Typography fontWeight={"bold"} fontSize={16}>
                          {initialVehicleDetails.chassi_veiculo
                            ? "V.I.N"
                            : "Placa"}
                        </Typography>
                        <Typography>
                          {initialVehicleDetails.chassi_veiculo ||
                            initialVehicleDetails.placa_veiculo}
                        </Typography>
                      </Grid>
                      <Grid
                        item={true}
                        xs={12}
                        sm={6}
                        md={4}
                        lg={4}
                        sx={{ borderRight: "1px solid #ccc", paddingBottom: 1 }}
                      >
                        <Typography fontWeight={"bold"} fontSize={16}>
                          Km Inicial
                        </Typography>
                        <Typography>
                          {initialVehicleDetails.km_veiculo}
                        </Typography>
                      </Grid>
                      <Grid
                        item={true}
                        xs={12}
                        sm={6}
                        md={4}
                        lg={4}
                        sx={{ paddingBottom: 1 }}
                      >
                        <Typography fontWeight={"bold"} fontSize={16}>
                          Local de Armazenagem
                        </Typography>
                        <Typography>
                          {initialVehicleDetails.descricao_local}
                        </Typography>
                      </Grid>
                    </Grid>
                  </>
                )}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </>
    );
  };
  const handleChange = (event: any) => {
    setPermissaoFoto(event.target.checked);
  };
  return (
    <ThemeProvider theme={defaultTheme}>
      <ContainerWrapper>
        <Container
          maxWidth="lg"
          sx={{
            mt: 10,
            mb: "auto",
            pb: checklistData?.cab.acoes.some(
              (a) => a.periodico_acao && idChecklistRespondido,
            )
              ? "230px"
              : "160px",
          }}
        >
          <Toolbar sx={{ mb: 1, mt: -2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                "&:hover": {
                  cursor: "pointer",
                  color: "red",
                },
              }}
              onClick={() => navigate("/veiculos")}
            >
              <IconButton
                edge="start"
                color="inherit"
                aria-label="voltar"
                sx={{ color: "inherit" }}
              >
                <ArrowBack />
              </IconButton>
              <Typography
                variant="h6"
                component="div"
                sx={{ color: "inherit", mt: 0.2 }}
              >
                Voltar
              </Typography>
            </Box>
          </Toolbar>
          {checklistData && (
            <>
              <Typography
                variant="h3"
                fontWeight={"bold"}
                textAlign={"center"}
                mb={8}
              >
                {checklistData.checklist.descricao_checklist}
              </Typography>

              <Grid>{renderCabDetails()}</Grid>
              {checklistData.checklist.legenda && (
                <Box mt={5}>
                  <Typography
                    variant="h5"
                    bgcolor="black"
                    color="white"
                    textAlign="center"
                    height="auto"
                    mb={0}
                    sx={{ paddingTop: "10px" }}
                  >
                    {checklistData.checklist.legenda}
                  </Typography>
                </Box>
              )}
              {checklistData.cab.valor_checklist_respondido &&
                checklistData.cab.condicao_checklist_respondido && (
                  <Grid container alignItems="center" mt={2}>
                    <Grid item xs={12}>
                      <Paper sx={{ border: 1, height: "auto" }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "100%",
                          }}
                        >
                          <Typography
                            variant="h6"
                            fontWeight="bold"
                            bgcolor={"black"}
                            color={"white"}
                            textAlign={"center"}
                            sx={{ width: "100%", py: 1 }}
                          >
                            Dados Comerciais
                          </Typography>
                        </Box>

                        <Grid container spacing={2} mt={1} px={1}>
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            sx={{
                              borderRight: { sm: "1px solid #ccc", xs: "none" },
                              paddingBottom: 1,
                              textAlign: "center",
                            }}
                          >
                            <Typography
                              fontWeight="bold"
                              fontSize={16}
                              textAlign="center"
                            >
                              Condição Comercial
                            </Typography>
                            <Typography
                              sx={{
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                textAlign: "center",
                              }}
                            >
                              {checklistData.cab.condicao_checklist_respondido}
                            </Typography>
                          </Grid>
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            sx={{
                              paddingBottom: 1,
                              textAlign: "center",
                            }}
                          >
                            <Typography
                              fontWeight="bold"
                              fontSize={16}
                              textAlign="center"
                            >
                              Valor de Venda
                            </Typography>
                            <Typography textAlign="center">
                              {formatCurrency2(
                                checklistData.cab.valor_checklist_respondido.toString(),
                              )}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  </Grid>
                )}

              {renderChecklistRespondido()}
              {acao?.entrega_acao ? (
                <Typography
                  variant="h6"
                  component="div"
                  sx={{ color: "inherit", mt: 0.2 }}
                >
                  AUTORIZA FOTOS NO MOMENTO DA ENTREGA{" "}
                  <Checkbox
                    defaultChecked
                    color="success"
                    onChange={handleChange}
                  />
                </Typography>
              ) : null}
              {checklistData.checklist.grupo_perguntas.map((grupo: any) => (
                <>
                  <Box key={grupo.id} mb={4} mt={10}>
                    <Grid container spacing={2}>
                      <Grid item={true} xs={12}>
                        <Typography
                          variant="h5"
                          bgcolor="black"
                          color="white"
                          textAlign="center"
                          height="auto"
                          mt={-4}
                          sx={{ paddingTop: "10px" }}
                        >
                          {grupo.descricao_checklist_grupo}
                        </Typography>
                        {grupo.ajuda_checklist_grupo && (
                          <Box
                            mb={0}
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            p={1}
                            border={2}
                          >
                            <Typography
                              variant="h4"
                              sx={{ flex: 1, fontWeight: "bold" }}
                              textAlign="center"
                            >
                              {grupo.ajuda_checklist_grupo}
                            </Typography>
                          </Box>
                        )}
                        {grupo.imagem_checklist_grupo && (
                          <Box
                            mb={0}
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            p={1}
                            border={2}
                          >
                            <img
                              src={`${process.env.REACT_APP_API_DOMAIN}/storage/${grupo.imagem_checklist_grupo}`}
                              alt={`Imagem do grupo: ${grupo.descricao_checklist_grupo}`}
                              style={{
                                maxWidth: "100%",
                                maxHeight: "300px",
                                margin: "10px auto",
                                display: "block",
                              }}
                            />
                          </Box>
                        )}

                        {grupo.perguntas.map((pergunta: any) => (
                          <Box
                            key={pergunta.id}
                            mb={0}
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            p={1}
                            border={2}
                          >
                            <Grid item xs={6} container alignItems="center">
                              {checklistData?.checklist
                                .obrigatorio_foto_pergunta_checklist !==
                                "sem_foto" && (
                                <IconButton
                                  onClick={() =>
                                    handleOpenFotoPerguntaModal(pergunta.id)
                                  }
                                  sx={{
                                    color:
                                      perguntaFotos[pergunta.id]?.length > 0
                                        ? "green"
                                        : "black",
                                    marginRight: 1,
                                  }}
                                  disabled={
                                    isCameraDisabled(pergunta.id) ||
                                    !isPerguntaRespondida(pergunta.id)
                                  }
                                >
                                  <CameraAlt />
                                </IconButton>
                              )}

                              <Typography
                                variant="h6"
                                sx={{
                                  flex: 2,
                                  fontSize: 16,
                                }}
                              >
                                {pergunta.descricao_checklist_pergunta}
                                {pergunta.obrigatorio_checklist_pergunta ===
                                  true && (
                                  <Typography
                                    component="span"
                                    color="red"
                                    ml={1}
                                    fontSize={23}
                                  >
                                    *
                                  </Typography>
                                )}
                              </Typography>
                            </Grid>

                            <Grid
                              item
                              xs={6}
                              container
                              justifyContent="flex-end"
                            >
                              {pergunta.respostas.map((resposta: any) => (
                                <React.Fragment key={resposta.id}>
                                  {resposta.descricao_checklist_resposta ===
                                  "Desc" ? (
                                    <TextField
                                      variant="outlined"
                                      label="Digite a descrição"
                                      value={
                                        responses[pergunta.id]?.textResponse ||
                                        ""
                                      }
                                      onChange={(event) =>
                                        handleTextResponse(event, pergunta.id)
                                      }
                                      sx={{ marginLeft: 10 }}
                                    />
                                  ) : (
                                    <IconButton
                                      sx={{
                                        color:
                                          responses[pergunta.id]
                                            ?.iconResponse ===
                                          resposta.valor_checklist_respostas
                                            ? resposta.descricao_checklist_resposta ===
                                              "Done"
                                              ? "green"
                                              : resposta.descricao_checklist_resposta ===
                                                  "Block"
                                                ? "red"
                                                : resposta.descricao_checklist_resposta ===
                                                    "NA"
                                                  ? "#ff780a"
                                                  : "blue"
                                            : "rgba(0, 0, 0, 0.38)",
                                      }}
                                      onClick={() =>
                                        handleIconClick(
                                          resposta.descricao_checklist_resposta,
                                          pergunta.id,
                                        )
                                      }
                                    >
                                      {resposta.descricao_checklist_resposta ===
                                      "Block" ? (
                                        <Close sx={{ fontSize: 40 }} />
                                      ) : resposta.descricao_checklist_resposta ===
                                        "Done" ? (
                                        <RadioButtonUnchecked
                                          sx={{ fontSize: 40 }}
                                        />
                                      ) : resposta.descricao_checklist_resposta ===
                                        "NA" ? (
                                        <ChangeHistory sx={{ fontSize: 40 }} />
                                      ) : (
                                        resposta.descricao_checklist_resposta
                                      )}
                                    </IconButton>
                                  )}
                                </React.Fragment>
                              ))}
                            </Grid>
                          </Box>
                        ))}
                        {grupo.rodape_checklist_grupo && (
                          <Typography
                            variant="h6"
                            bgcolor="black"
                            color="white"
                            textAlign="center"
                            height={50}
                            sx={{ paddingTop: "13px" }}
                          >
                            {grupo.rodape_checklist_grupo}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                </>
              ))}

              {observations.map((observation, index) => (
                <Box key={index} sx={{ mb: 4 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <ErrorOutline sx={{ mr: 1 }} />
                      <Typography variant="h6">{`Observação ${
                        index + 1
                      }`}</Typography>
                    </Box>
                    <IconButton
                      edge="end"
                      color="error"
                      aria-label="delete"
                      onClick={() => handleRemoveObservation(index)}
                    >
                      <Delete sx={{ fontSize: 25 }} />
                    </IconButton>
                  </Box>

                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    variant="outlined"
                    placeholder="Digite sua observação"
                    value={observation}
                    onChange={(e) =>
                      handleObservationChange(index, e.target.value)
                    }
                  />
                </Box>
              ))}
              <Button
                fullWidth
                variant="contained"
                startIcon={<Add />}
                color="primary"
                sx={{
                  height: 50,
                  marginTop: 5,
                  borderRadius: 2,
                  alignItems: "center",
                  ":hover": {
                    bgcolor: "#5c5c5c",
                  },
                }}
                onClick={handleAddObservation}
              >
                {observations.length === 0
                  ? "Adicionar Observação"
                  : "Adicionar Nova Observação"}
              </Button>
              <Box>
                {capturedImages.length > 0 && (
                  <Grid item={true} container spacing={2}>
                    {capturedImages.map(
                      (image, index) =>
                        index % 2 === 0 && (
                          <React.Fragment key={index}>
                            <Grid item={true} xs={6}>
                              <img
                                src={image}
                                alt={`Captured ${index}`}
                                style={{
                                  maxWidth: "100%",
                                  display: "block",
                                }}
                              />
                            </Grid>
                            {capturedImages[index + 1] && (
                              <Grid item={true} xs={6}>
                                <img
                                  src={capturedImages[index + 1]}
                                  alt={`Captured ${index + 1}`}
                                  style={{
                                    maxWidth: "100%",
                                    display: "block",
                                  }}
                                />
                              </Grid>
                            )}
                          </React.Fragment>
                        ),
                    )}
                  </Grid>
                )}
              </Box>

              {capturedImages.length > 0 && (
                <Box textAlign="center">
                  <Button
                    onClick={clearImages}
                    style={{ marginTop: "10px", color: "#303030" }}
                  >
                    Limpar Imagens
                  </Button>
                </Box>
              )}
              <AppBar
                position="fixed"
                sx={{
                  top: "auto",
                  bottom: 0,
                  backgroundColor: "white",
                  height: checklistData?.cab.acoes.some(
                    (a) => a.periodico_acao && idChecklistRespondido,
                  )
                    ? 230
                    : 160,
                }}
              >
                <Grid item={true} container p={2}>
                  <Grid
                    item={true}
                    sx={{
                      paddingRight:
                        checklistData.checklist.assinatura_checklist === true &&
                        checklistData.checklist.fotos_checklist === true
                          ? 1
                          : 0,
                    }}
                    xs={
                      checklistData.checklist.assinatura_checklist === true &&
                      checklistData.checklist.fotos_checklist === true
                        ? 6
                        : 12
                    }
                  >
                    {checklistData.checklist.fotos_checklist === true && (
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => handleOpenCamera("geral")}
                        sx={{
                          height: 50,
                          marginTop: 2,
                          backgroundColor: fotoGeralTirada ? "green" : "black",
                          borderRadius: 2,
                          alignItems: "center",
                          ":hover": {
                            bgcolor: "#5c5c5c",
                          },
                        }}
                      >
                        <Typography>Enviar Foto</Typography>
                        <CameraAlt sx={{ marginLeft: 1 }} />
                      </Button>
                    )}
                  </Grid>
                  <Grid
                    item={true}
                    sx={{
                      paddingRight:
                        checklistData.checklist.assinatura_checklist === true &&
                        checklistData.checklist.fotos_checklist === true
                          ? 0
                          : 1,
                    }}
                    xs={
                      checklistData.checklist.assinatura_checklist === true &&
                      checklistData.checklist.fotos_checklist === true
                        ? 6
                        : 12
                    }
                  >
                    {checklistData.checklist.assinatura_checklist === true && (
                      <Button
                        variant="contained"
                        fullWidth
                        sx={{
                          height: 50,
                          marginTop: 2,
                          backgroundColor: signed ? "green" : "black",
                          ":hover": {
                            bgcolor: signed ? "#5c5c5c" : "#303030",
                          },
                          borderRadius: 2,
                          alignItems: "center",
                        }}
                        onClick={handleOpenSign}
                      >
                        <Typography>
                          {signed ? "Checklist Assinado" : "Assinar Checklist"}
                        </Typography>
                        <BorderColor sx={{ marginLeft: 1 }} />
                      </Button>
                    )}
                  </Grid>

                  <Grid item container spacing={2} justifyContent="center">
                    <Grid item xs={12} sm={12} md={12}>
                      <LoadingButton
                        variant="contained"
                        loading={loading}
                        fullWidth
                        sx={{
                          height: 50,
                          marginTop: 2,
                          backgroundColor: "black",
                          borderRadius: 2,
                          alignItems: "center",
                          ":hover": {
                            bgcolor: "#5c5c5c",
                          },
                        }}
                        onClick={() => {
                          setLoading(true);
                          const { allAnswered, missingPhotos } =
                            areAllRequiredQuestionsAnswered();

                          if (!allAnswered) {
                            toast.error(
                              "Preencha todas as perguntas obrigatórias antes de salvar o checklist.",
                            );
                            setLoading(false);
                          } else if (missingPhotos) {
                            toast.error(
                              "É obrigatório capturar fotos das perguntas obrigatórias!",
                            );
                            setLoading(false);
                          } else {
                            handleSendResponses();
                          }
                        }}
                        loadingPosition="end"
                        loadingIndicator={
                          <CircularProgress sx={{ color: "black" }} size={20} />
                        }
                      >
                        <Typography color={loading ? "black" : "white"}>
                          Salvar
                        </Typography>
                        <Checklist
                          sx={{
                            marginLeft: 1,
                            color: loading ? "black" : "white",
                          }}
                        />
                      </LoadingButton>
                    </Grid>
                    {checklistData?.cab.acoes.some(
                      (a) => a.periodico_acao && idChecklistRespondido,
                    ) && (
                      <Grid item xs={12}>
                        <LoadingButton
                          variant="contained"
                          loading={loading}
                          fullWidth
                          sx={{
                            height: 50,
                            marginTop: 0,
                            backgroundColor: "#1976d2",
                            borderRadius: 2,
                            alignItems: "center",
                            ":hover": {
                              bgcolor: "#1565c0",
                            },
                          }}
                          onClick={handleGeneratePDF}
                        >
                          <Typography color="white">Gerar PDF</Typography>
                          <PictureAsPdf
                            sx={{
                              marginLeft: 1,
                            }}
                          />
                        </LoadingButton>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
                <Modal open={isKmModalOpen} onClose={handleCloseKmModal}>
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      bgcolor: "background.paper",
                      border: "2px solid #000",
                      boxShadow: 24,
                      p: 4,
                      width: "80%",
                      maxWidth: 400,
                    }}
                  >
                    <Typography variant="h6" mb={2}>
                      Editar KM Final
                    </Typography>
                    <TextField
                      fullWidth
                      label="KM Final"
                      value={tempKmFinal}
                      onChange={(e) => setTempKmFinal(e.target.value)}
                      sx={{ mb: 2 }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleSaveKmFinal}
                      sx={{ mr: 2 }}
                    >
                      Salvar
                    </Button>
                    <Button variant="outlined" onClick={handleCloseKmModal}>
                      Cancelar
                    </Button>
                  </Box>
                </Modal>
                <Modal open={isSignOpen}>
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      bgcolor: "background.paper",
                      border: "2px solid #000",
                      boxShadow: 24,
                      p: 4,
                      width: "80%",
                      maxWidth: 800,
                      height: "80%",
                      maxHeight: 350,
                    }}
                  >
                    <IconButton
                      onClick={handleCloseSign}
                      sx={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        backgroundColor: "red",
                        color: "white",
                        ":hover": {
                          bgcolor: "#5c5c5c",
                        },
                      }}
                    >
                      <Close />
                    </IconButton>
                    <Typography
                      variant="h6"
                      color="black"
                      height={50}
                      sx={{
                        paddingTop: "13px",
                      }}
                    >
                      Assinatura do Cliente
                    </Typography>

                    {signed ? (
                      <>
                        <img
                          src={signed}
                          alt="Assinatura"
                          style={{
                            maxWidth: "100%",
                            height: "auto",
                            marginBottom: 45,
                          }}
                        />
                        <Button
                          variant="contained"
                          onClick={handleClearSignature}
                          sx={{
                            mt: 7,
                            display: "block",
                            margin: "0 auto",
                            backgroundColor: "#303030",
                            ":hover": {
                              bgcolor: "#5c5c5c",
                            },
                          }}
                        >
                          Apagar Assinatura
                        </Button>
                      </>
                    ) : (
                      <>
                        <Box
                          mb={5}
                          display="flex"
                          alignItems="center"
                          borderBottom={2}
                        >
                          <CanvasDraw
                            brushColor={"black"}
                            brushRadius={1}
                            canvasWidth={1200}
                            canvasHeight={200}
                            hideInterface={true}
                            hideGrid={true}
                            ref={canvasRef}
                          />
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginTop: 5,
                          }}
                        >
                          <Button
                            variant="contained"
                            onClick={handleExport}
                            sx={{
                              backgroundColor: "#303030",
                              ":hover": {
                                bgcolor: "#5c5c5c",
                              },
                              marginRight: 2,
                            }}
                          >
                            Salvar Assinatura
                          </Button>
                          <Button
                            variant="contained"
                            onClick={() => {
                              canvasRef.current.clear();
                            }}
                            sx={{
                              backgroundColor: "#FF0000",
                              ":hover": {
                                bgcolor: "#AA0000",
                              },
                            }}
                          >
                            Limpar Assinatura
                          </Button>
                        </Box>
                      </>
                    )}
                  </Box>
                </Modal>
                <Modal open={isCameraOpen}>
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      bgcolor: "background.paper",
                      border: "2px solid #000",
                      boxShadow: 24,
                      p: 4,
                      width: "80%",
                      maxWidth: 800,
                      height: "90%",
                      maxHeight: 700,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <IconButton
                      onClick={handleCloseCamera}
                      sx={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        backgroundColor: "red",
                        color: "white",
                        ":hover": {
                          bgcolor: "#5c5c5c",
                        },
                      }}
                    >
                      <Close />
                    </IconButton>
                    <Webcam
                      audio={false}
                      ref={webcamRef}
                      screenshotFormat="image/png"
                      videoConstraints={videoConstraints}
                      style={{
                        width: "100%",
                        height: "auto",
                        marginBottom: 50,
                      }}
                    />
                    <Grid
                      container
                      spacing={2}
                      sx={{ mt: { xs: -13, sm: -10, md: -6 } }}
                    >
                      <Grid item xs={12} sm={6} md={6}>
                        <Button
                          variant="contained"
                          onClick={capture}
                          sx={{
                            display: "block",
                            width: "100%",
                            maxWidth: "100%",
                            backgroundColor: "#303030",
                            ":hover": {
                              bgcolor: "#5c5c5c",
                            },
                          }}
                        >
                          Capturar Imagem
                        </Button>
                      </Grid>
                      <Grid item xs={12} sm={6} md={6}>
                        <IconButton
                          onClick={toggleCameraFacing}
                          sx={{
                            width: "100%",
                            maxWidth: "100%",
                            backgroundColor: "#303030",
                            color: "white",
                            ":hover": {
                              bgcolor: "#5c5c5c",
                            },
                            borderRadius: 1,
                          }}
                        >
                          {isFrontFacing ? <CameraFront /> : <CameraRear />}
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Box>
                </Modal>
                <Modal
                  open={isFotoPerguntaModalOpen}
                  onClose={handleCloseFotoPerguntaModal}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      bgcolor: "background.paper",
                      border: "2px solid #000",
                      boxShadow: 24,
                      p: 4,
                      width: "80%",
                      maxWidth: 800,
                      maxHeight: "90%",
                      overflowY: "auto",
                    }}
                  >
                    <IconButton
                      onClick={handleCloseFotoPerguntaModal}
                      sx={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        backgroundColor: "red",
                        color: "white",
                        ":hover": {
                          bgcolor: "#5c5c5c",
                        },
                      }}
                    >
                      <Close />
                    </IconButton>
                    <Typography variant="h6" mb={2}>
                      Fotos da Pergunta
                    </Typography>

                    {perguntaIdSelecionada !== null &&
                    perguntaFotos[perguntaIdSelecionada]?.length > 0 ? (
                      <Grid container spacing={2}>
                        {perguntaFotos[perguntaIdSelecionada].map(
                          (foto, index) => (
                            <Grid item xs={4} key={index}>
                              <Box position="relative">
                                <img
                                  src={foto}
                                  alt={`Foto ${
                                    index + 1
                                  } da pergunta ${perguntaIdSelecionada}`}
                                  style={{
                                    maxWidth: "100%",
                                    height: "auto",
                                    borderRadius: 4,
                                  }}
                                />
                                <IconButton
                                  onClick={() =>
                                    handleExcluirFoto(
                                      perguntaIdSelecionada,
                                      index,
                                    )
                                  }
                                  sx={{
                                    position: "absolute",
                                    top: 0,
                                    right: 0,
                                    backgroundColor: "rgba(255, 0, 0, 0.7)",
                                    color: "white",
                                    ":hover": {
                                      bgcolor: "rgba(255, 0, 0, 0.9)",
                                    },
                                  }}
                                >
                                  <Delete />
                                </IconButton>
                              </Box>
                            </Grid>
                          ),
                        )}
                      </Grid>
                    ) : (
                      <Typography
                        variant="body1"
                        color="textSecondary"
                        textAlign="center"
                      >
                        Nenhuma foto capturada para esta pergunta!
                      </Typography>
                    )}

                    <Box mt={2} textAlign="center">
                      <Button
                        variant="contained"
                        onClick={() =>
                          handleOpenCamera("pergunta", perguntaIdSelecionada)
                        }
                        sx={{
                          backgroundColor: "#303030",
                          ":hover": {
                            bgcolor: "#5c5c5c",
                          },
                        }}
                      >
                        Capturar Foto
                      </Button>
                    </Box>
                  </Box>
                </Modal>
                <Modal
                  aria-labelledby="transition-modal-title"
                  aria-describedby="transition-modal-description"
                  open={openModal}
                  closeAfterTransition
                >
                  <Box>
                    <Fade in={openModal}>
                      <Box
                        sx={{
                          backgroundColor: "#fff",
                          borderRadius: 2,
                          boxShadow: 24,
                          p: 4,
                          maxWidth: "90vw",
                          margin: 3,
                          height: "auto",
                          marginTop: "20vh",
                          textAlign: "center",
                        }}
                      >
                        <Grid container spacing={2} p={2} mt={-6}>
                          <Grid item xs={12}>
                            {loading && (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                  mt: 2,
                                }}
                              >
                                <Typography variant="h6">
                                  Aguarde seu PDF está sendo gerado!
                                </Typography>
                              </Box>
                            )}
                            {loading && (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                  mt: 3,
                                }}
                              >
                                <CircularProgress sx={{ color: "black" }} />
                              </Box>
                            )}
                          </Grid>
                          {pdfUrl && (
                            <>
                              <Button
                                onClick={() => {
                                  setOpenModal(false);
                                  setPdfGenerated(false);
                                  setPdfUrl("");
                                }}
                                sx={{
                                  backgroundColor: "red",
                                  color: "white",
                                  ":hover": {
                                    bgcolor: "#5c5c5c",
                                  },
                                }}
                              >
                                <Typography
                                  sx={{
                                    typography: {
                                      sm: "h6",
                                      xs: "caption",
                                      lg: "h6",
                                      md: "h6",
                                    },
                                  }}
                                >
                                  Fechar
                                </Typography>
                              </Button>
                              <Grid
                                item
                                xs={12}
                                sx={{ height: { xs: 250, sm: 350, lg: 500 } }}
                              >
                                <iframe
                                  src={`${pdfUrl}`}
                                  title="PDF"
                                  width="100%"
                                  height="100%"
                                />
                                <Typography
                                  sx={{
                                    typography: {
                                      sm: "h6",
                                      xs: "caption",
                                      lg: "h6",
                                      md: "h6",
                                    },
                                  }}
                                >
                                  PDF Gerado com sucesso!
                                </Typography>
                              </Grid>
                            </>
                          )}
                        </Grid>
                      </Box>
                    </Fade>
                  </Box>
                </Modal>
              </AppBar>
              <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              >
                <Alert
                  severity="success"
                  variant="filled"
                  sx={{ width: "100%" }}
                >
                  Foto capturada com sucesso!
                </Alert>
              </Snackbar>
            </>
          )}
        </Container>
        <Box
          sx={{
            height: checklistData?.cab.acoes.some(
              (a) => a.periodico_acao && idChecklistRespondido,
            )
              ? 230
              : 160,
          }}
        />
      </ContainerWrapper>
    </ThemeProvider>
  );
};

export default ChecklistPage;
